import { Flex, Text } from '@enechain/ecloud-designsystem'

type PageIntroProps = {
  /**
   * 見出し (h2)。`textStyle="pageTitle"` (24px / 700 / high-emphasis) 固定で描画する
   * (ReactNode 不可 = 型で typography 手書きの抜け道を塞ぐ。PageBody.heading と同じ判断)。
   *
   * 省略時は見出し行を描画しない。パンくず末尾 (PageLayout の `bold: true`) が
   * 画面名を兼ねる単一画面では説明文だけを置き、広域マスタのように 1 画面の中で
   * 対象マスタが切り替わる画面でだけ見出しを渡す。
   */
  heading?: string
  /**
   * 画面・タブの説明文。design `.page-description`
   * (font-size md / line-height 1.6 / color high-emphasis) 準拠。
   *
   * 色は **high-emphasis 固定** (design css/components.css:1290-1295 に
   * 「primary 固定。mid にしない」と明記)。既存画面には mid / low-emphasis で
   * 手組みしたものがあるが、本 component 経由に寄せる際に high へ揃える。
   *
   * `heading` と違い ReactNode を許容する。design の `.page-description` は
   * 半数の画面で内部リンク (`.ecl-text-link`) や `<br>` を本文に含むため
   * (admin/bid-limits.html:41 / admin/loss-rate.html:81 /
   * admin/case-templates.html:223 / admin/weather-forecast-points.html:44)、
   * string 固定にすると当該画面が本 component を使えず手組みに戻ってしまう。
   * typography / 色は下の wrapper `<Text>` が持ち、子は継承するだけなので
   * 「役割で書く」規約は保たれる (子側で fontSize / color を書かないこと)。
   */
  description: React.ReactNode
}

/**
 * 画面冒頭の 見出し + 説明文 (design `.occto-content-title` + `.page-description`)。
 *
 * design 側は `.page-description` を「各画面で独自の desc class を作らず、ここを使う」
 * 全画面共通クラスとして定義している (design/ui-feasibility/components/page-layout-utilities.md §1)。
 * frontend も同様に、画面ごとに `<Text color=... fontSize=...>` を手組みせず本 component を使う。
 *
 * 縦 spacing は 見出し-説明文間 8px のみを内部固定し、外側 margin は持たない
 * (縦 spacing の所有権は置き場所側 = PageBody の gap 等に一本化する。
 * docs/adr/frontend-skeleton-closed-style-props.md と同じ方針で style props も公開しない)。
 */
export const PageIntro = ({
  heading,
  description,
}: PageIntroProps): React.ReactElement => (
  <Flex flexDirection="column" gap={2}>
    {heading !== undefined && (
      <Text as="h2" textStyle="pageTitle" margin={0} color="Text.highEmphasis">
        {heading}
      </Text>
    )}
    <Text textStyle="body" color="Text.highEmphasis">
      {description}
    </Text>
  </Flex>
)
